import React, { useState } from "react";
import { Box, Button, Chip, CircularProgress, Collapse, FormControlLabel, Switch, TextField, Typography } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import { Contact, ContactExtData, ResidentialData } from "../../../interfaces/DTO/crm/contact";
import { usePartialUpdateContact } from "../../../hooks/crm/useContacts";

/** small flags as SVG - flag emojis do not render on Windows */
export const IsraelFlag: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" viewBox="0 0 30 22" sx={{ width: size, height: size * 0.73, borderRadius: "2px", boxShadow: 1, flexShrink: 0 }} aria-hidden>
    <rect width="30" height="22" fill="#fff" />
    <rect y="2" width="30" height="3" fill="#0038b8" />
    <rect y="17" width="30" height="3" fill="#0038b8" />
    <path d="M15 6.6 L18.6 12.9 H11.4 Z M15 15.4 L11.4 9.1 H18.6 Z" fill="none" stroke="#0038b8" strokeWidth="1" />
  </Box>
);

export const UsaFlag: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <Box component="svg" viewBox="0 0 30 20" sx={{ width: size, height: size * 0.66, borderRadius: "2px", boxShadow: 1, flexShrink: 0 }} aria-hidden>
    <rect width="30" height="20" fill="#fff" />
    {[0, 2, 4, 6, 8, 10, 12].map((i) => (
      <rect key={i} y={(i * 20) / 13} width="30" height={20 / 13} fill="#b22234" />
    ))}
    <rect width="13" height={(7 * 20) / 13} fill="#3c3b6e" />
    {[0, 1, 2].map((r) =>
      [0, 1, 2, 3].map((c) => <circle key={`${r}-${c}`} cx={2 + c * 3} cy={2 + r * 3.4} r="0.7" fill="#fff" />)
    )}
  </Box>
);

/** the citizenship being edited */
export interface CitizenshipDraft {
  israeliOnly: boolean;
  country_of_birth: string;
  foreign_resident: boolean;
  additional_country_resident: boolean;
  residence_country: string;
  us_citizenship: boolean;
  usa_ssn: string;
}

export const toCitizenshipDraft = (contact: Contact): CitizenshipDraft => {
  const r = contact.data?.residential_data ?? {};
  const draft: CitizenshipDraft = {
    israeliOnly: false,
    country_of_birth: r.country_of_birth ?? "",
    foreign_resident: r.foreign_resident === true,
    additional_country_resident: r.additional_country_resident === true,
    residence_country: r.residence_country ?? "",
    us_citizenship: r.us_citizenship === true,
    usa_ssn: contact.usa_ssn ?? "",
  };
  draft.israeliOnly = !draft.foreign_resident && !draft.additional_country_resident && !draft.us_citizenship && !draft.residence_country;
  return draft;
};

/**
 * the contact fields to save for a citizenship draft - the whole data (a partial update replaces it as is) with
 * only what is filled in, and the SSN (a US citizen only)
 */
export const citizenshipFields = (contact: Contact, draft: CitizenshipDraft): { data: ContactExtData; usa_ssn: string } => {
  const residential: ResidentialData = {};
  if (draft.country_of_birth.trim()) residential.country_of_birth = draft.country_of_birth.trim();
  if (!draft.israeliOnly) {
    if (draft.foreign_resident) residential.foreign_resident = true;
    if (draft.additional_country_resident) residential.additional_country_resident = true;
    if ((draft.foreign_resident || draft.additional_country_resident) && draft.residence_country.trim())
      residential.residence_country = draft.residence_country.trim();
    if (draft.us_citizenship) residential.us_citizenship = true;
  }
  const data: ContactExtData = { ...(contact.data ?? {}) };
  if (Object.keys(residential).length > 0) data.residential_data = residential;
  else delete data.residential_data;
  const ssn = !draft.israeliOnly && draft.us_citizenship ? draft.usa_ssn.trim() : "";
  // null clears the column
  return { data, usa_ssn: (ssn || null) as unknown as string };
};

/** the summary line - flags and the countries */
export const CitizenshipSummary: React.FC<{ contact: Contact }> = ({ contact }) => {
  const current = toCitizenshipDraft(contact);
  const otherCountry =
    (current.foreign_resident || current.additional_country_resident) && (current.residence_country || "מדינה נוספת");
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
      <IsraelFlag />
      {current.israeliOnly ? (
        <Typography>אזרח ישראלי בלבד</Typography>
      ) : (
        <>
          <Typography>אזרח ישראלי</Typography>
          {current.us_citizenship && (
            <Chip
              icon={<Box sx={{ display: "flex", pr: 1 }}><UsaFlag size={18} /></Box>}
              label={`אזרח אמריקאי${contact.usa_ssn ? ` · SSN ${contact.usa_ssn}` : ""}`}
              variant="outlined"
              sx={{ fontSize: "1rem", fontWeight: 700, height: 34 }}
            />
          )}
          {otherCountry && <Chip icon={<PublicIcon />} label={`תושב ${otherCountry}`} variant="outlined" />}
        </>
      )}
      {current.country_of_birth && (
        <Typography variant="body2" color="text.secondary">
          · נולד ב{current.country_of_birth}
        </Typography>
      )}
    </Box>
  );
};

/** the citizenship fields - controlled; "אזרח ישראלי בלבד" hides the rest */
export const CitizenshipEditor: React.FC<{ value: CitizenshipDraft; onChange: (value: CitizenshipDraft) => void }> = ({
  value,
  onChange,
}) => {
  const set = (fields: Partial<CitizenshipDraft>) => onChange({ ...value, ...fields });
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <FormControlLabel
        control={<Switch checked={value.israeliOnly} onChange={(e) => set({ israeliOnly: e.target.checked })} />}
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IsraelFlag size={18} /> אזרח ישראלי בלבד
          </Box>
        }
      />
      <Collapse in={!value.israeliOnly}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pr: 1, borderRight: "3px solid", borderColor: "divider" }}>
          <FormControlLabel
            control={<Switch checked={value.us_citizenship} onChange={(e) => set({ us_citizenship: e.target.checked })} />}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <UsaFlag size={18} /> אזרחות אמריקאית
              </Box>
            }
          />
          {value.us_citizenship && (
            <TextField
              size="small"
              label="מספר ביטוח לאומי אמריקאי (SSN)"
              value={value.usa_ssn}
              onChange={(e) => set({ usa_ssn: e.target.value })}
              inputProps={{ dir: "ltr" }}
              sx={{ maxWidth: 320, backgroundColor: "background.paper" }}
            />
          )}
          <FormControlLabel
            control={<Switch checked={value.foreign_resident} onChange={(e) => set({ foreign_resident: e.target.checked })} />}
            label="תושב חוץ"
          />
          <FormControlLabel
            control={
              <Switch
                checked={value.additional_country_resident}
                onChange={(e) => set({ additional_country_resident: e.target.checked })}
              />
            }
            label="תושב מדינה נוספת חוץ מישראל"
          />
          {(value.foreign_resident || value.additional_country_resident) && (
            <TextField
              size="small"
              label="מדינת התושבות"
              value={value.residence_country}
              onChange={(e) => set({ residence_country: e.target.value })}
              sx={{ maxWidth: 320, backgroundColor: "background.paper" }}
            />
          )}
        </Box>
      </Collapse>
      <TextField
        size="small"
        label="ארץ לידה"
        value={value.country_of_birth}
        onChange={(e) => set({ country_of_birth: e.target.value })}
        placeholder="ישראל"
        sx={{ maxWidth: 320, backgroundColor: "background.paper" }}
      />
    </Box>
  );
};

/**
 * אזרחות ותושבות – כרטיס קומפקטי עם מצב עריכה משלו: בברירת מחדל "אזרח ישראלי בלבד"; אזרחות אמריקאית (עם דגל
 * ו-SSN) ותושבות במדינה נוספת מוצגות רק כשקיימות. נשמרים רק השדות שמולאו.
 */
const CitizenshipCard: React.FC<{ contact: Contact }> = ({ contact }) => {
  const updateContact = usePartialUpdateContact();
  const [draft, setDraft] = useState<CitizenshipDraft | null>(null);

  const save = async () => {
    if (!draft) return;
    await updateContact.mutateAsync({ id: contact.id!, fields: citizenshipFields(contact, draft) });
    setDraft(null);
  };

  return (
    // the same look as EditableFieldsBlock - the blocks above it
    <Box sx={{ width: "100%", p: 2, borderRadius: 2, boxShadow: 1, backgroundColor: "background.default" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <CitizenshipSummary contact={contact} />
        {!draft && (
          <Button size="small" onClick={() => setDraft(toCitizenshipDraft(contact))}>
            ערוך
          </Button>
        )}
      </Box>

      <Collapse in={!!draft} unmountOnExit>
        {draft && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <CitizenshipEditor value={draft} onChange={setDraft} />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                onClick={save}
                disabled={updateContact.isPending}
                startIcon={updateContact.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                שמור
              </Button>
              <Button variant="outlined" onClick={() => setDraft(null)} disabled={updateContact.isPending}>
                חזור
              </Button>
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default CitizenshipCard;
